import { userEndpoints, withPrimaryDistrict } from "store/api/userEndpoints";

/**
 * The signed-in user lives in the RTK Query cache (getCurrentUser), and the
 * open district is one of the user's districts (the primary one).
 *
 * Read them with `useSelector(getUserStoreData)` / `useSelector(selectDistrictData)`.
 * After a change, put the API's copy back:
 *   cacheCurrentUser(user)           the whole user (login, onboarding step)
 *   patchCurrentUser({ field })      some fields of the user
 *   saveUserItem("districts", d)     a district/campus of the user (replace or add)
 *   patchUserItem("districts", id, { field })  some fields of one of them
 */

// Shape of the user before it has loaded (and after sign out)
export const EMPTY_USER = Object.freeze({
  id: null,
  first_name: null,
  last_name: null,
  name: null,
  email: null,
  phone_number: null,
  countryCode: "+1",
  title: null,
  type: "Internal",
  notes: "",
  auth_provider: null,
  auth_provider_id: null,
  is_location_enabled: false,
  is_camera_enabled: false,
  is_notification_enabled: false,
  date_of_birth: null,
  profile_pic: null,
  pendingScreen: null,
  is_onboarding_complete: false,
  districts: [],
  district_id: null,
  primary_district_id: null,
  campuses: [],
  building: [],
  tags: [],
  role_id: 0,
  role_name: "",
  admin_panel_access: false,
  toc_desktop_app_access: false,
  end_user_mobile_app_access: false,
  district_setup_access: false,
});

// Shape of the district before it has loaded (and for a user without districts)
export const EMPTY_DISTRICT = Object.freeze({
  account_owner_id: null,
  address: null,
  building_count: 0,
  campus_count: 0,
  city: null,
  createdAt: null,
  created_by_id: null,
  deletedAt: null,
  id: null,
  email: null,
  idp_detail: null,
  invite_code: null,
  lockdown_alert_method: null,
  is_admin_invite_skipped: false,
  is_auth_provider_skipped: false,
  logo_url: null,
  name: null,
  num_staff: 0,
  num_students: 0,
  pocs: [],
  state: null,
  updatedAt: null,
  zip_code: null,
});

const selectCurrentUserResult = userEndpoints.endpoints.getCurrentUser.select();

/** The signed-in user once loaded, otherwise EMPTY_USER */
export const getUserStoreData = (state) => selectCurrentUserResult(state).data ?? EMPTY_USER;

// The open district: the primary one, or the first if the primary is not one of them
const openDistrictOf = (user) =>
  user.districts?.find((d) => d.id === user.primary_district_id) ?? user.districts?.[0];

/** The open district (one of the user's districts), otherwise EMPTY_DISTRICT */
export const selectDistrictData = (state) => openDistrictOf(getUserStoreData(state)) ?? EMPTY_DISTRICT;

/** Puts the whole signed-in user into the cache (login, signup, onboarding steps) */
export const cacheCurrentUser = (user) =>
  userEndpoints.util.upsertQueryEntries([
    { endpointName: "getCurrentUser", arg: undefined, value: withPrimaryDistrict(user) },
  ]);

const updateUser = (recipe) => userEndpoints.util.updateQueryData("getCurrentUser", undefined, recipe);

/** Changes some fields of the signed-in user */
export const patchCurrentUser = (fields) =>
  updateUser((user) => {
    Object.assign(user, fields);
  });

/**
 * Replaces a district or campus of the user with the API's copy (by id), or
 * adds it. The first district becomes the primary one if there is none.
 */
export const saveUserItem = (keyName, item) =>
  updateUser((user) => {
    if (!item?.id) return;
    const list = user[keyName] ?? [];
    const index = list.findIndex((el) => el.id === item.id);
    if (index >= 0) list[index] = item;
    else list.push(item);
    user[keyName] = list;
    if (keyName === "districts" && !user.primary_district_id) {
      user.primary_district_id = list[0].id;
    }
  });

/** Changes some fields of a district or campus of the user (by id) */
export const patchUserItem = (keyName, id, fields) =>
  updateUser((user) => {
    const item = user[keyName]?.find((el) => el.id === id);
    if (item) Object.assign(item, fields);
  });
